/**
 * Reprodução de áudio local (modo escolha musical — P0.3). Carrega o Blob da
 * tabela `foto` (genérica de Blobs) e toca. Nada sai do aparelho.
 *
 * Segurança sensorial: a reprodução só acontece por ação direta da criança/cuidador
 * (toque), nunca automática/inesperada (ver §12 da spec).
 */
import { db } from "../schema.dexie";
import type { ExperienciaMusical } from "./types";

/** A 1ª mídia de áudio da experiência (se houver). */
async function refAudio(exp: ExperienciaMusical): Promise<string | null> {
  for (const ref of exp.midias) {
    const f = await db.foto.get(ref);
    if (f?.blob.type.startsWith("audio")) return ref;
  }
  return null;
}

export async function temAudio(exp: ExperienciaMusical): Promise<boolean> {
  return (await refAudio(exp)) !== null;
}

/**
 * Conjunto dos ids de experiências que têm áudio tocável agora. Usado pelo modo
 * escolha pra PREFERIR (não exigir) cards que dão feedback sonoro celebratório.
 */
export async function idsComAudio(
  exps: ExperienciaMusical[],
): Promise<Set<string>> {
  const pares = await Promise.all(
    exps.map(async (e) => [e.id, await temAudio(e)] as const),
  );
  return new Set(pares.filter(([, tem]) => tem).map(([id]) => id));
}

/**
 * Toca o 1º clipe de áudio da experiência. Devolve o elemento (pra parar) ou null
 * se não houver áudio. Revoga o object URL ao terminar.
 */
export async function tocarAudio(
  exp: ExperienciaMusical,
): Promise<HTMLAudioElement | null> {
  const ref = await refAudio(exp);
  if (!ref) return null;
  const f = await db.foto.get(ref);
  if (!f) return null;
  const url = URL.createObjectURL(f.blob);
  const audio = new Audio(url);
  audio.addEventListener("ended", () => URL.revokeObjectURL(url));
  try {
    await audio.play();
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
  return audio;
}
